import { Callout, Checkbox, DefaultButton, Dropdown, IconButton, IContextualMenuItem, IDropdownOption, IRenderFunction, Pivot, PivotItem, PrimaryButton, SearchBox, Stack, Sticky, StickyPositionType, Text, TextField, useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import {
    ConstrainMode, DetailsList,
    DetailsListLayoutMode, IDetailsHeaderProps, IDetailsListProps, IDetailsListStyles
} from '@fluentui/react/lib/DetailsList';
import { appStyles } from 'global/Application';
import { IExtendedColumn } from 'model/IExtendedColumn';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import LinkMenu from '../LinkMenu';
import { ICustomDetailListConfiguration } from './ICustomDetailListConfiguration';
import { IExtendedColumnIdentifier } from './IExtendedColumnIdentifier';


interface ICustomDetailListProps extends IDetailsListProps {
    defaultGridConfig?: ICustomDetailListConfiguration;
    itemsPaged?: any[],
    canFilter?: boolean;
    canSort?: boolean;
    quickSearchColumnKey?: string;
    gridConfig: [ICustomDetailListConfiguration, React.Dispatch<React.SetStateAction<ICustomDetailListConfiguration>>];
    showNoResultPrompt?: boolean;
    showLimitedResultPrompt?: boolean;
    hasPageSettings?: boolean;
    queryMenuItems?: IContextualMenuItem[];
    queryMenuSelectedKey?: string;
    onExtract?: () => void;
    heightUnconstrained?: boolean;
}

const ex = (e: any) => e.preventDefault();

export const DefaultGridConfig: (() => ICustomDetailListConfiguration) = () => {
    return {
        sort: [],
        filter: [],
        pageSize: 10,
        page: 1,
    }
}

export const UpdateColumnByConfig = (column: IExtendedColumn, gridConfig: ICustomDetailListConfiguration) => {
    if (gridConfig) {
        const sortProperty = gridConfig.sort?.find(q => q?.column?.key === column.key);
        if (sortProperty) {
            column.isSorted = true;
            column.isSortedDescending = sortProperty.isSortedDescending;
        }

        const filterProperty = gridConfig.filter?.find(q => q.column?.key === column.key);
        if (filterProperty) {
            column.isFiltered = true;
        }
    }
}

const CustomDetailList: React.FunctionComponent<ICustomDetailListProps> = (props) => {
    const theme = useTheme();

    const buttonId = useId('callout-settings');
    const [callout, setCallout] = useState(false);

    const optionsColumn = useMemo(() => {
        const options: IDropdownOption<IExtendedColumnIdentifier>[] = [];

        if (callout) {
            props.columns?.map(c => {
                options.push({
                    key: c.key,
                    text: c.name,
                    data: { key: c.key }
                });
            });
        }

        return options;

    }, [callout, props.columns]);

    const optionsPagination = useMemo(() => {
        const options: IDropdownOption[] = [];

        options.push({
            key: -1,
            text: 'None',
        });

        options.push({
            key: 8,
            text: '8 Items/Page'
        });


        options.push({
            key: 10,
            text: '10 Items/Page'
        });


        options.push({
            key: 15,
            text: '15 Items/Page'
        });


        options.push({
            key: 20,
            text: '20 Items/Page/Compact'
        });


        return options;

    }, []);


    const [gridConfigProp, setGridConfigProp] = props.gridConfig;

    const [gridConfig, setGridConfig] = useState(structuredClone(gridConfigProp));


    useEffect(() => {
        if (callout) {
            const gridConfigSnapshot = {
                ...DefaultGridConfig(),
                ...structuredClone(gridConfigProp)
            };

            if (gridConfigSnapshot.filter.length === 0)
                gridConfigSnapshot.filter.push({} as any);

            if (gridConfigSnapshot.sort.length === 0)
                gridConfigSnapshot.sort.push({} as any);

            setGridConfig(gridConfigSnapshot);
        }

    }, [callout]);

    const pageSize = gridConfigProp?.pageSize ?? 10;

    const page_number_max = pageSize === -1
        ? 1
        : Math.ceil((props.items.length) / pageSize);


    const pagedGridStyles = useMemo(() => {
        const pageHeightLargeLookup: Record<string, string | undefined> = {
            '-1': undefined,
            '5': '400px',
            '10': '480px',
            '15': '700px',
            '20': '690px',
        }

        const pageHeightCompactLookup: Record<string, string | undefined> = {
            '-1': undefined,
            '5': '200px',
            '10': '375px',
            '15': '400px',
            '20': '690px',
        }

        const pageHeightLookup = props.compact
            ? pageHeightCompactLookup
            : pageHeightLargeLookup;

        let minHeight = pageHeightLookup[gridConfigProp?.pageSize ?? '10'];
        let maxHeight = (gridConfigProp?.pageSize ?? 0) > 0 ? undefined : '60vh';

        if (props.heightUnconstrained) {
            minHeight = undefined;
            maxHeight = undefined;
        }

        const gridStyles: Partial<IDetailsListStyles> = {
            ...props.styles,
            root: {
                overflowX: 'scroll',
                selectors: {
                    '& [role=grid]': {
                        // this was the only way to get the grid not cause the parent to surpass max-width calculation
                        maxWidth: '50em',
                        
                        display: 'flex',
                        flexDirection: 'column',

                        alignItems: 'start',
                        maxHeight: maxHeight,
                        minHeight: minHeight
                    },
                },
                border: 'thin solid lightgray',
            },
            headerWrapper: {
                selectors: {
                    '.ms-DetailsHeader': {
                        paddingTop: '0px',
                    },
                }
            },
        };

        return gridStyles;
    }, [props.styles, gridConfigProp]);

    const oldGridStyles = useMemo(() => {

        let maxHeight: string | undefined = '60vh';

        if (props.heightUnconstrained) {
            maxHeight = undefined;
        }

        const gridStyles: Partial<IDetailsListStyles> = {
            ...props.styles,
            root: {
                overflowX: 'scroll',
                selectors: {
                    '& [role=grid]': {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        maxHeight: maxHeight,
                    },
                    '& [role=columnheader]': {
                        display: 'inline-block',
                        position: 'sticky',
                        top: '20px',
                        zIndex: 10000
                    },
                },
                border: 'thin solid lightgray',

            },
            headerWrapper: {
                flex: '0 0 auto',
                selectors: {
                    '.ms-DetailsHeader': {
                        paddingTop: '0px',

                    },

                }
            },
            contentWrapper: {
                flex: '1 1 auto',
                overflowY: 'auto',
                overflowX: 'hidden',
            },
        };

        return gridStyles;
    }, []);

    const defaultQueryMenuItems = useMemo(() => {
        const defaultQueryMenuItems: IContextualMenuItem[] = [
            {
                key: 'default',
                text: 'Default Query',
            },
        ];
        return defaultQueryMenuItems;
    }, []);

    const onRenderTopBar = () => {
        return <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
            <Stack.Item>
                <LinkMenu
                    menuItems={props.queryMenuItems ?? defaultQueryMenuItems}
                    selectedKey={props.queryMenuSelectedKey ?? "default"}
                />

            </Stack.Item>
            <Stack.Item>
                {/* blank to keep space */}
            </Stack.Item>
            <Stack.Item>
                <Stack horizontal horizontalAlign='end' verticalAlign='center'>
                    {props.quickSearchColumnKey &&
                        <Stack.Item>
                            <SearchBox
                                placeholder="Quick Search"
                                styles={{ root: { transform: 'scale(0.7,0.7)', transformOrigin: 'right', backgroundColor: 'transparent', width: '140px' } }}
                                onSearch={(newValue) => {
                                    setGridConfigProp(x => {
                                        const t = { ...x };
                                        if (newValue)
                                            t.filter = [{
                                                column: { key: props.quickSearchColumnKey! },
                                                value: newValue,
                                            }];
                                        else
                                            t.filter = [];
                                        return t;
                                    });
                                }}
                                onClear={(event) => {
                                    setGridConfigProp(x => {
                                        const t = { ...x };
                                        t.filter = [];
                                        return t;
                                    });
                                }}
                            />
                        </Stack.Item>
                    }

                    {props.showLimitedResultPrompt &&
                        <Stack.Item>
                            <Text variant="small" styles={{ root: { color: 'maroon' } }}>
                                Displaying limited results. Please refine your search criteria for more accurate results.
                            </Text>
                        </Stack.Item>
                    }
                    {/* {props.onExtract &&
                        <Stack.Item>
                            <IconButton
                                id={buttonId}
                                className="FooterIcon"
                                iconProps={{ iconName: "Generate" }}
                                styles={{
                                    icon: {
                                        fontSize: '12px',
                                        color: theme.palette.black,
                                    },
                                }}
                                onClick={() => {
                                    props.onExtract?.();
                                }}
                            />
                        </Stack.Item>
                    } */}
                    <Stack.Item>
                        <IconButton
                            id={buttonId}
                            className="FooterIcon"
                            iconProps={{ iconName: "Settings" }}
                            styles={{
                                icon: {
                                    fontSize: '12px',
                                    color: theme.palette.black,
                                },
                            }}
                            onClick={async () => {
                                setCallout(true);
                            }}
                        />
                    </Stack.Item>

                </Stack>

            </Stack.Item>
        </Stack>
    }

    // function ArrayWithDefault<T>(items: T[], defaultItem?: Partial<T>) {
    //     return items?.length > 0 ? items : [defaultItem as T ?? {} as T];
    // }

    const onRenderWrapper = (children: JSX.Element | JSX.Element[] | undefined) => {
        return <div style={{ padding: '8px' }}>
            {children}
        </div>
    }

    const onRenderSortPivot = () => {
        return <>
            {gridConfig.sort.map((sort, ix) => {
                return <Stack tokens={{ childrenGap: 4 }}>
                    <Dropdown
                        label="Field"
                        options={optionsColumn}
                        selectedKey={sort?.column?.key}
                        onChange={(event, option?: IDropdownOption<IExtendedColumnIdentifier>) => {
                            setGridConfig(t => {
                                const q = { ...t! };
                                q.sort = [...q.sort];
                                q.sort[ix].column = { key: option?.data?.key! };
                                return q;
                            });
                        }}
                    />
                    <Checkbox
                        label='Descending'
                        checked={sort.isSortedDescending}
                        onChange={(event, checked) => {
                            setGridConfig(t => {
                                const q = { ...t! };
                                q.sort = [...q.sort];
                                q.sort[ix].isSortedDescending = checked ?? false;
                                return q;
                            });
                        }}
                    />
                </Stack>;
            })}
        </>
    }

    const onRenderFilterPivot = () => {
        return <>
            {gridConfig.filter.map((filter, ix) => {
                return <Stack tokens={{ childrenGap: 8 }}>
                    <Dropdown
                        label="Field"
                        options={optionsColumn}
                        selectedKey={filter?.column?.key}
                        onChange={(event, option?: IDropdownOption<IExtendedColumnIdentifier>) => {
                            setGridConfig(t => {
                                const q = { ...t! };
                                q.filter = [...q.filter]
                                q.filter![ix].column = { key: option?.data?.key! };
                                return q;
                            });
                        }}
                    />
                    <TextField
                        autoComplete='off'
                        label='Value'
                        value={filter.value ?? ''}
                        onChange={(event, newValue) => {
                            setGridConfig(t => {
                                const q = { ...t! };
                                q.filter = [...q.filter]
                                q.filter![ix].value = newValue ?? null;
                                return q;
                            });
                        }}

                    />
                </Stack>
            })}

            {/* 
            Multi column sort has a bug in algorithm, filter works ok
            <DefaultButton
                text='Add'
                onClick={() => {
                    setGridConfigSnapshot(t => {
                        return { ...t, filter: [...t.filter, { column: { key: optionsFilter[0].data?.key! }, value: '' }] };
                        // return { ...t, filter: [...t.filter, { column: { key: optionsFilter[0].data?.key }, value: '' }] }
                    })
                }}
            />
            */}
        </>
    }

    const onRenderSettingsPivot = () => {
        return <Dropdown
            label="Pagination"
            options={optionsPagination}
            selectedKey={gridConfig.pageSize}
            onChange={(event, option, index) => {
                setGridConfig(t => {
                    const q = { ...t! };
                    q.pageSize = option?.key as number;
                    return q;
                });

            }}
        />;
    }

    const onRenderCallout = () => {
        if (callout)
            return <Callout
                className={appStyles.calloutNarrow}
                role="dialog"
                beakWidth={0}
                gapSpace={0}
                target={`#${buttonId}`}
                onDismiss={() => setCallout(false)}
                setInitialFocus
            >
                <div style={{ minHeight: '220px' }}>
                    <Pivot styles={{ root: {} }}>
                        <PivotItem headerText="Filter">
                            {onRenderWrapper(onRenderFilterPivot())}
                        </PivotItem>

                        <PivotItem headerText="Sort">
                            {onRenderWrapper(onRenderSortPivot())}
                        </PivotItem>

                        {props.hasPageSettings !== false &&
                            <PivotItem headerText="Settings">
                                {onRenderWrapper(onRenderSettingsPivot())}
                            </PivotItem>
                        }

                    </Pivot>
                </div>

                {onRenderWrapper(onRenderCalloutFooter())}

            </Callout>;
    }

    const onRenderCalloutFooter = () => {
        return <Stack tokens={{ childrenGap: 15 }} style={{}}>
            <Stack.Item >
                <Stack horizontal style={{ float: 'right' }} tokens={{ childrenGap: 15 }}>
                    <PrimaryButton
                        text='Apply'
                        onClick={() => {
                            const t: ICustomDetailListConfiguration = {
                                ...gridConfig,
                                filter: gridConfig.filter.filter(t => !!t?.column?.key && !!t.value),
                                sort: gridConfig.sort.filter(t => !!t.column),
                                page: 1,
                            };

                            setGridConfigProp(t);
                            setGridConfig(structuredClone(t));
                            setCallout(false);
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => {
                            setCallout(false);
                        }}
                    />
                </Stack>
            </Stack.Item>
        </Stack>;
    }

    const resetPage = () => {
        setGridConfigProp(t => { return { ...t, page: 1 } });
    }

    const nextPage = () => {
        setGridConfigProp(t => {

            let page = Math.min(t.page + 1, page_number_max);

            if (page === 0)
                page = 1;

            return { ...t!, page: page }
        });
    }

    const prevPage = () => {
        setGridConfigProp(t => {
            let page = Math.max(1, t.page - 1);

            if (page === 0)
                page = 1;

            return { ...t!, page: page }
        });
    }

    const onRenderPageScroll = () =>
        <IconButton
            iconProps={{ iconName: "ScrollUpDown" }}
            title={'Scroll using your mouse wheel to navigate pages.'}
            onMouseEnter={() => {
                window.addEventListener("wheel", ex, { passive: false });
            }}
            onMouseLeave={() => {
                window.removeEventListener("wheel", ex);
            }}
            onWheel={(event) => {
                if (event.deltaY > 0 || event.deltaX > 0)
                    nextPage();
                else if (event.deltaY < 0 || event.deltaX < 0)
                    prevPage()
            }}
            styles={{
                root: { 'transform': 'rotate(90deg)' },
                icon: {
                    fontSize: '12px',
                    color: theme.palette.black
                },
            }}
        />;

    const onRenderPagination = () => {
        if (pageSize === -1)
            return <></>;

        return <Stack horizontal horizontalAlign='space-between' verticalAlign='center' styles={{ root: { marginTop: '3px', marginBottom: '8px' } }}>
            <Stack.Item >
                {/* <Text variant='xSmall'>{1} - {10} of {30} ({25} selected)</Text> */}
                {(props.itemsPaged ?? props.items).length !== 0 &&
                    <Text variant='xSmall'>
                        {((gridConfigProp.page - 1) * pageSize) + 1} - {Math.min(gridConfigProp.page * pageSize, props.items.length)} of {props.items.length}
                    </Text>
                }
                {(props.itemsPaged ?? props.items).length === 0 &&
                    <Text variant='xSmall'>
                        0 - 0 of 0
                    </Text>
                }
            </Stack.Item>
            {props.selection?.isAllSelected() && page_number_max > 1 &&
                <Stack.Item styles={{ root: { marginRight: 'auto' } }}>
                    <Text variant='xSmall' styles={{ root: { color: 'darkred', marginLeft: '10px' } }}>More items may be selected than displayed.</Text>
                </Stack.Item>
            }
            <Stack.Item >
                <Stack horizontal verticalAlign='center'>
                    <IconButton
                        className="FooterIcon"
                        iconProps={{ iconName: "Previous" }}
                        title={'First Page'}
                        styles={{
                            icon: {
                                fontSize: '12px',
                                color: theme.palette.black
                            },
                        }}
                        onClick={(event) => {
                            resetPage();
                        }}
                    />
                    <IconButton
                        className="FooterIcon"
                        iconProps={{ iconName: "ReplyAlt" }}
                        title={'Previous Page'}
                        onClick={(event) => {
                            prevPage();
                        }}
                        styles={{
                            icon: {
                                fontSize: '12px',
                                color: theme.palette.black
                            },
                        }}
                    />
                    <Text variant='xSmall'>Page {gridConfigProp.page}/{page_number_max}</Text>
                    <IconButton
                        className="FooterIcon ArrowRight"
                        iconProps={{ iconName: "ReplyAlt" }}
                        title={'Next Page'}
                        onClick={(event) => {
                            nextPage();
                        }}
                        styles={{
                            root: { 'transform': 'rotate(180deg)' },
                            icon: {
                                fontSize: '12px',
                                color: theme.palette.black
                            },
                        }}
                    />
                    {onRenderPageScroll()}
                </Stack>
            </Stack.Item>
        </Stack>;
    }

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (headerProps, defaultRender) => {
        return <Sticky stickyPosition={StickyPositionType.Header} stickyBackgroundColor="transparent"> <div>{defaultRender!(headerProps)}</div></Sticky>
    };

    const onRenderDetailList = () => {
        // deprecate this ASAP
        if (pageSize === -1)
            return <DetailsList
                {...props}
                items={props.items}
                columns={props.columns}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.unconstrained}
                onRenderDetailsHeader={onRenderDetailsHeader}
                styles={oldGridStyles}
            />;

        return <DetailsList
            {...props}
            items={props.itemsPaged ?? props.items}
            columns={props.columns}
            compact={props.compact ?? gridConfigProp.pageSize === 20}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            constrainMode={ConstrainMode.unconstrained}
            onShouldVirtualize={(props) => false}
            styles={pagedGridStyles}
        />;

    }

    const onRenderPrompts = () => {
        if (!props.showLimitedResultPrompt && !props.showNoResultPrompt)
            return <></>;

        return <div style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
        }}>
            <Stack horizontal styles={{ root: { padding: '0px 20px 10px 20px' } }} >
                {props.showNoResultPrompt &&
                    <Stack.Item>
                        <Text variant="small">
                            No results found.
                        </Text>
                    </Stack.Item>
                }

            </Stack>
        </div>
    }

    return <div
        style={{ marginTop: '4px' }}
    // onMouseEnter={() => {
    //     window.addEventListener("wheel", ex, { passive: false });
    // }}
    // onMouseLeave={() => {
    //     window.removeEventListener("wheel", ex);
    // }}
    // onWheel={(event) => {
    //     if (event.deltaY > 0 || event.deltaX > 0)
    //         nextPage();
    //     else if (event.deltaY < 0 || event.deltaX < 0)
    //         prevPage()
    // }}
    >
        {onRenderTopBar()}
        {onRenderDetailList()}
        {onRenderPrompts()}
        {onRenderPagination()}
        {onRenderCallout()}
    </div>;

};

export default CustomDetailList;